import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { DataGridProProps } from "@mui/x-data-grid-pro";
import { format } from "date-fns";
import {
  BULLETIN_STATUS_COLORS,
  BULLETIN_STATUS_LABELS,
} from "entities/bulletin/constants";
import {
  Bulletin,
  BulletinStatus,
  UpdateStatusParams,
} from "entities/bulletin/types";
import React from "react";
import Chip from "shared/components/Chip";
import DropdownList from "shared/components/DropdownList";
import FlexCenter from "shared/components/Layout/components/FlexCenter";
import { DrawerHookResult } from "shared/hooks/useDrawerState";
import {
  COLOR_PRIMARY_MAIN,
  COLOR_RED,
  COLOR_YELLOW,
} from "shared/theme/colors";
import { ArrowForwardIcon } from "shared/theme/icons/ArrowForwardIcon";

type ColumnsProps = {
  bulletinBoardDetailsDrawer: DrawerHookResult<string>;
  handleStatus: (values: UpdateStatusParams) => void;
  handleDeleteBulletin: () => void;
  setSelectedBulletin: (value: Bulletin) => void;
  readOnly: boolean;
};

export const getBulletinColumns = ({
  bulletinBoardDetailsDrawer,
  handleStatus,
  handleDeleteBulletin,
  setSelectedBulletin,
  readOnly,
}: ColumnsProps): DataGridProProps["columns"] => {
  const renderActions = (bulletin: Bulletin) => {
    if (readOnly) {
      return (
        <FlexCenter
          onClick={() => bulletinBoardDetailsDrawer.openDrawer(bulletin.id)}
        >
          <ArrowForwardIcon color={COLOR_PRIMARY_MAIN} />
        </FlexCenter>
      );
    }

    const statusTitle =
      bulletin.status === BulletinStatus.Active ? "Inactive" : "Published";

    return (
      <DropdownList
        items={[
          {
            title: "Edit",
            onClick: () => bulletinBoardDetailsDrawer.openDrawer(bulletin.id),
          },
          {
            title: statusTitle,
            onClick: () => {
              handleStatus({
                bulletinId: bulletin.id,
                status:
                  bulletin.status === BulletinStatus.Active
                    ? BulletinStatus.Draft
                    : BulletinStatus.Active,
              });
            },
          },
          {
            title: "Delete",
            style: "remove",
            onClick: () => {
              setSelectedBulletin(bulletin);
              handleDeleteBulletin();
            },
          },
        ]}
      />
    );
  };

  return [
    {
      field: "isHighPriority",
      headerName: "Priority",
      editable: false,
      sortable: true,
      flex: 0.5,
      renderCell: (params) => {
        return (
          <Box display="flex" alignItems="center" gap="0.5rem">
            <Box
              bgcolor={Boolean(params.value) ? COLOR_RED : COLOR_YELLOW}
              height="0.5rem"
              borderRadius="50%"
              width="0.5rem"
            />
            <Typography>
              {Boolean(params.value) ? "High" : "Standard"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "title",
      headerName: "Title",
      editable: false,
      sortable: true,
      flex: 2,
    },
    {
      field: "publishedAt",
      headerName: "Published Date",
      editable: false,
      sortable: true,
      flex: 1,
      renderCell: (params) => {
        if (params.value) {
          return format(params.value.toDate(), "dd MMM yyyy, HH:mm");
        }

        return "-";
      },
    },
    {
      field: "status",
      headerName: "Status",
      editable: false,
      sortable: true,
      flex: 0.5,
      renderCell: (params) => {
        const status = params.value as BulletinStatus;
        return (
          <Chip
            title={BULLETIN_STATUS_LABELS[status]}
            bgColor={BULLETIN_STATUS_COLORS[status]}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      editable: false,
      align: "right",
      headerAlign: "right",
      flex: 0.5,
      renderCell: (params) => renderActions(params.row),
    },
  ];
};
